import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

const IMPERSONATION_STORAGE_KEY = 'paila-impersonation';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    studentId: string;
    email: string;
    name: string;
  } | null;
  idToken: string | null;
  isAdmin: boolean;
  impersonatedStudentId: string | null;
  effectiveStudentId: string | null;
  setImpersonation: (studentEmail: string | null) => void;
  preferredLanguage: string;
  setPreferredLanguage: (code: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { authStatus, signOut: amplifySignOut } = useAuthenticator();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [impersonatedStudentId, setImpersonatedStudentId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [preferredLanguage, setPreferredLanguageState] = useState<string>(() => {
    try {
      return localStorage.getItem('paila-language') || 'en';
    } catch {
      return 'en';
    }
  });

  const signOut = useCallback(async () => {
    // Set flag so ProtectedRoute knows not to auto-redirect to SSO
    sessionStorage.setItem('intentionalLogout', 'true');
    await amplifySignOut();
    navigate('/logged-out');
  }, [amplifySignOut, navigate]);

  const refreshSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const tokens = session.tokens;

      if (tokens?.idToken) {
        const token = tokens.idToken.toString();
        setIdToken(token);

        const payload = tokens.idToken.payload;
        setUser({
          studentId: (payload['custom:student_id'] as string) || '',
          email: (payload['email'] as string) || '',
          name:
            `${payload['given_name'] || ''} ${payload['family_name'] || ''}`.trim() ||
            (payload['email'] as string) ||
            '',
        });

        const groups = payload['cognito:groups'];
        const groupList = Array.isArray(groups)
          ? groups
          : typeof groups === 'string'
            ? [groups]
            : [];
        setIsAdmin(groupList.includes('paila-admins'));
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }, []);

  // Load user data on auth status change
  useEffect(() => {
    const loadUserData = async () => {
      if (authStatus === 'authenticated') {
        await refreshSession();

        // Fetch server-side language preference
        try {
          const apiUrl = window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (token) {
            const resp = await fetch(`${apiUrl}/preferences`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
              const data = await resp.json();
              const lang = data.preferred_language || 'en';
              setPreferredLanguageState(lang);
              try { localStorage.setItem('paila-language', lang); } catch { /* ignore */ }
            }
          }
        } catch {
          // Fall back to localStorage value (already set as initial state)
        }
      } else {
        setUser(null);
        setIdToken(null);
        setIsAdmin(false);
        setImpersonatedStudentId(null);
        try { sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY); } catch { /* ignore */ }
      }
      setIsLoading(false);
    };

    loadUserData();
  }, [authStatus, refreshSession]);

  // Refresh token every 45 minutes (Cognito ID tokens expire after 60 minutes)
  // Also refresh when the tab becomes visible again (browser throttles timers in background tabs)
  useEffect(() => {
    if (authStatus !== 'authenticated') return;

    const REFRESH_INTERVAL_MS = 45 * 60 * 1000;
    const interval = setInterval(refreshSession, REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authStatus, refreshSession]);

  const setImpersonation = useCallback((studentEmail: string | null) => {
    if (!studentEmail) {
      setImpersonatedStudentId(null);
      try { sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY); } catch { /* ignore */ }
      return;
    }
    // Extract student ID from email prefix (e.g. u2646354@uel.ac.uk -> u2646354)
    const studentId = studentEmail.includes('@')
      ? studentEmail.split('@')[0]
      : studentEmail;
    setImpersonatedStudentId(studentId);
    try { sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, studentId); } catch { /* ignore */ }
  }, []);

  const setPreferredLanguage = useCallback(async (code: string) => {
    setPreferredLanguageState(code);
    try { localStorage.setItem('paila-language', code); } catch { /* ignore */ }

    // Persist to server
    try {
      const apiUrl = window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        await fetch(`${apiUrl}/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ preferred_language: code }),
        });
      }
    } catch {
      // localStorage already updated as cache — server sync can retry next time
    }
  }, []);

  const effectiveStudentId = impersonatedStudentId || user?.studentId || null;

  const value: AuthContextType = {
    isAuthenticated: authStatus === 'authenticated',
    isLoading,
    user,
    idToken,
    isAdmin,
    impersonatedStudentId,
    effectiveStudentId,
    setImpersonation,
    preferredLanguage,
    setPreferredLanguage,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
